<script setup lang="ts">
import { computed, ref } from "vue";

import { useEditSiteConsumptionInfosMutation } from "@/composables/kili/consumption/mutations/use-edit-site-consumption-infos-mutation";
import { useGetSiteConsumptionInfosQuery } from "@/composables/kili/consumption/queries/use-get-site-consumption-infos-query";
import {
  EElectricityTarif,
  TEditSiteConsumptionInfosBody,
} from "@/data/api/kili/consumption/types/consumption-infos.type";
import { type TSiteDetails } from "@/data/api/kili/sites/types/sites.type";
import { useTypedIntl } from "@/locales/typed-intl";

import Divider from "primevue/divider";
import Skeleton from "primevue/skeleton";

import EditSiteDetailsDialog from "@/components/tools/site-details/edit-site-details/edit-site-details-dialog.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import ColoredBadge from "@/components/ui/colored-badge.vue";

interface IProps {
  siteId: number;
  siteDetails?: TSiteDetails;
  isLoading?: boolean;
}

const props = defineProps<IProps>();

const { t, k, n } = useTypedIntl();

const _siteId = computed<number>(() => props.siteId);

const { data: siteConsumptionInfos, isPending: isSiteConsumptionInfosPending } =
  useGetSiteConsumptionInfosQuery(_siteId);

const { mutateAsync: editSiteConsumptionInfos, isPending: isEditSiteCOnsumptionInfosPending } =
  useEditSiteConsumptionInfosMutation();

const isEditSiteDetailsDialogVisible = ref<boolean>(false);

async function handleEditSiteConsumptionInfos(infos: TEditSiteConsumptionInfosBody) {
  await editSiteConsumptionInfos({
    siteId: _siteId,
    body: infos,
  }).then(() => (isEditSiteDetailsDialogVisible.value = false));
}
</script>

<template>
  <div class="mb-6 mt-4 rounded-md p-6 bg-white flex flex-col gap-6">
    <div class="flex gap-4 items-center">
      <ColoredBadge :name="siteDetails?.name" />
      <div class="flex flex-col gap-1">
        <h4>
          {{ siteDetails?.name }}
        </h4>
      </div>
    </div>

    <div class="flex gap-2 items-end">
      <div class="flex flex-1">
        <div class="flex flex-col gap-2">
          <small class="uppercase">
            {{ t(k.tools.siteManagement.siteDetails.surfaceArea) }}
          </small>
          <Skeleton
            v-if="isSiteConsumptionInfosPending"
            class="!h-12"
          />
          <div
            v-else
            class="bg-gray-100 rounded-md p-2 w-fit"
          >
            <h3 class="text-lg">
              {{ typeof siteConsumptionInfos?.siteArea === "number" ? siteConsumptionInfos?.siteArea : "--" }}
            </h3>
          </div>
        </div>

        <Divider layout="vertical" />

        <div class="flex flex-col gap-2">
          <small class="uppercase">
            {{ t(k.tools.siteManagement.siteDetails.kWhCost) }}
          </small>
          <Skeleton
            v-if="isSiteConsumptionInfosPending"
            class="!h-12 !w-[20rem]"
          />
          <div
            v-else
            class="bg-gray-100 rounded-md p-2 w-fit"
          >
            <div class="flex gap-6">
              <div
                v-for="tarif in Object.values(EElectricityTarif)"
                :key="tarif"
                class="flex gap-2 items-center"
              >
                <small class="uppercase text-xs">
                  {{ t(k.global.tarifType[tarif]) }}
                </small>
                <h3 class="text-lg">
                  {{
                    typeof siteConsumptionInfos?.electricityPrices[tarif] === "number"
                      ? n(siteConsumptionInfos?.electricityPrices[tarif], { maximumFractionDigits: 4 })
                      : "--"
                  }}
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      <HospitalityButton
        aria-controls="edit-site-details-dialog"
        :aria-expanded="isEditSiteDetailsDialogVisible"
        aria-haspopup="dialog"
        data-test-id="edit-site-details-button"
        is-icon-only
        :label="t(k.global.misc.edit)"
        size="xs"
        @click="isEditSiteDetailsDialogVisible = true"
      >
        <i class="pi pi-pen-to-square" />
      </HospitalityButton>
    </div>
  </div>

  <EditSiteDetailsDialog
    id="edit-site-details-dialog"
    v-model="isEditSiteDetailsDialogVisible"
    :is-loading="isEditSiteCOnsumptionInfosPending"
    :site-consumption-infos="siteConsumptionInfos"
    :site-name="props.siteDetails?.name"
    @submit="handleEditSiteConsumptionInfos($event)"
  />
</template>
